import { DateTime } from '@ksyos/date-time';
import { AvatarBlock, Box, Button, Card, FormGroup, Heading } from '@ksyos/design-system';
import { useBlocker, useNavigate, useParams } from '@ksyos/router';
import { pick } from 'lodash';
import { useEffect, useState } from 'react';
import type { OrganizationTypeWithLinkedProfessions } from '@common/enums';
import type { IIdentifierDeclaration, IRegistrationCodesAdd } from '@common/interfaces/';
import type { EmploymentAdd } from '@common/interfaces/employmentAdd';
import type { PractitionerRegistrationCodesValidated } from '@common/validation/practitioner';
import type { ValidationErrors } from '@frontend/utils';
import { ManagerAuthorization, ValidationErrorCode } from '@common/enums';
import { convertOrganizationTypeToOrganizationTypeWithLinkedProfessions } from '@common/utils/convertOrganizationTypeToOrganizationTypeWithLinkedProfessions';
import { formatString } from '@common/utils/formatString';
import { getDisplayInitials } from '@common/utils/getDisplayInitials';
import { getDisplayName } from '@common/utils/getDisplayName';
import { padStartBigCode } from '@common/utils/padStartBigCode';
import { practitionerEmploymentFrontendSchema } from '@common/validation/practitioner';
import { OverlapMessage } from '@frontend/components/elements/common';
import { getSelectedLanguage } from '@frontend/translate';
import {
    areThereRegistrationCodeErrors,
    assertHasAuthorization,
    checkDuplicatedRegistrationCodes,
    emptyValidationErrors,
    getSpecialismCodeByProfession,
    hasOneAuthorization,
    OverlapChecker,
    useAbortableFetch,
    validate,
} from '@frontend/utils';
import { KgbSuccessPage } from '../../elements/KgbSuccessPage';
import { ConfirmationModal } from '../../elements/modals/ConfirmationModal';
import { OrganizationSelection } from '../../elements/OrganizationSelection';
import { OverlayTemplate } from '../../templates/OverlayTemplate';
import { EmploymentDetailsAddForm } from './EmploymentDetailsAddForm';

interface Practitioner extends IRegistrationCodesAdd {
    emailAddress: string;
    nameFamily: string;
    nameGiven: string | null;
    nameInfix: string | null;
    profilePictureUrl?: string;
}

interface IOverlap {
    endDate: string | null;
    id: string;
    startDate: string;
}

const today = DateTime.now().format('YYYY-MM-DD');

export function EmploymentAdd() {
    const translate = getSelectedLanguage();
    const { id } = useParams();
    const navigate = useNavigate();
    const overlapChecker = new OverlapChecker(`/api/employments/${id}/organizations`);

    const [overlap, setOverlap] = useState<IOverlap | null>(null);
    const [organizationId, setOrganizationId] = useState<string | undefined>();
    const [organizationName, setOrganizationName] = useState<string | undefined>();
    const [organizationType, setOrganizationType] = useState<OrganizationTypeWithLinkedProfessions | null>(null);
    const [practitioner, setPractitioner] = useState<Practitioner>({
        emailAddress: '',
        identifierDeclaration: null,
        identifierRegistrationBig: null,
        identifierRegistrationKp: null,
        nameFamily: '',
        nameGiven: null,
        nameInfix: null,
        profilePictureUrl: '',
    });
    const [profilePractitionerRegistrationCodes, setProfilePractitionerRegistrationCodes] =
        useState<IRegistrationCodesAdd>({
            identifierDeclaration: null,
            identifierRegistrationBig: null,
            identifierRegistrationKp: null,
        });
    const [employment, setEmployment] = useState<EmploymentAdd>({
        emailAddress: null,
        identifierDeclarationPractitionerId: null,
        isOrganizationManager: null,
        profession: null,
        specialismCode: null,
        startDate: today,
        zorgmailNummer: null,
    });
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [areValidationsActivated, setAreValidationsActivated] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessPage, setShowSuccessPage] = useState(false);
    const abortableFetch = useAbortableFetch();
    const [isValidatingAndWantsToSave, setIsValidatingAndWantsToSave] = useState(false);
    const [isBlocked, setIsBlocked] = useState(true);
    const blocker = useBlocker(isBlocked);

    const defaultReturnUrl = `/practitioners/${id}/profile`;

    assertHasAuthorization(ManagerAuthorization.practitionerAddEdit);

    useEffect(() => {
        loadPractitioner();
    }, [id]);

    useEffect(() => {
        if (areValidationsActivated) {
            validateOnChange();
        }
    }, [employment, practitioner]);

    useEffect(() => {
        if (areValidationsActivated) {
            validateOnChange(['identifierDeclaration']);
        }
    }, [practitioner.identifierDeclaration]);

    useEffect(() => {
        if (areValidationsActivated) {
            validateOnChange(['identifierRegistrationBig']);
        }
    }, [practitioner.identifierRegistrationBig]);

    useEffect(() => {
        if (isValidatingAndWantsToSave && !overlap) {
            if (errors.size > 0) {
                setIsValidatingAndWantsToSave(false);

                return;
            }
            save();
        }
    }, [overlap, isValidatingAndWantsToSave]);

    useEffect(() => {
        if (organizationId) {
            loadOrganizationType();
        } else {
            setOrganizationType(null);
        }
    }, [organizationId]);

    if (showSuccessPage) {
        return (
            <OverlayTemplate>
                <KgbSuccessPage
                    buttonText={translate.employment.backToProfile}
                    linkTo={`/practitioners/${id}/profile`}
                    text={formatString(translate.employment.employmentAdded, [
                        getDisplayName(practitioner, false, 'nameGivenNameFamily'),
                        organizationName!,
                    ])}
                    title={translate.organization.successful}
                />
            </OverlayTemplate>
        );
    } else {
        return (
            <OverlayTemplate
                leftButtons={
                    <Button
                        isDisabled={isSaving}
                        onClick={() => navigate(defaultReturnUrl)}
                        variant="outline"
                    >
                        {translate.common.cancelling}{' '}
                    </Button>
                }
                rightButtons={
                    <Button
                        isDisabled={isSaving || !!overlap || errors.size > 0}
                        onClick={() => validateAndSaveEmployment()}
                        variant="solid"
                    >
                        {translate.personalSettings.save}{' '}
                    </Button>
                }
            >
                <Box
                    display="flex"
                    flexDirection={'column'}
                    gap={6}
                    pb={4}
                >
                    <Heading size="xlarge">{translate.organization.add}</Heading>
                    <AvatarBlock
                        backgroundColor="white"
                        imageUrl={practitioner?.profilePictureUrl}
                        initials={getDisplayInitials(practitioner)}
                        size="medium"
                        title={practitioner ? getDisplayName(practitioner, false, 'nameGivenNameFamily') : ''}
                    />
                </Box>

                <>
                    <Card>
                        <Card.Header title={translate.organization.select} />
                        <Card.Body>
                            {overlap && (
                                <OverlapMessage
                                    endDate={overlap.endDate}
                                    link={
                                        hasOneAuthorization(ManagerAuthorization.employmentsOld)
                                            ? `/employments/${overlap.id}?` +
                                              `organizationId=${organizationId}` +
                                              `&practitionerId=${id}`
                                            : null
                                    }
                                    message={translate.employment.overlapMessage}
                                    startDate={overlap.startDate}
                                />
                            )}
                            <FormGroup
                                hasErrors={errors.has('organizationId')}
                                isRequired
                                label={translate.organization.organization}
                            >
                                <OrganizationSelection
                                    error={errors.get('organizationId')}
                                    isActive={true}
                                    isKsyosOrganizationExcluded
                                    onChange={async (value, label) => {
                                        setOrganizationId(value);
                                        setOrganizationName(label);
                                        checkOverlap(value);
                                        handleChangeEmployment({ identifierDeclarationPractitionerId: null });
                                    }}
                                    placeholder="Zoeken op naam of AGB-code..."
                                    value={organizationId}
                                />
                            </FormGroup>
                        </Card.Body>
                    </Card>

                    <EmploymentDetailsAddForm
                        employment={employment}
                        errors={errors}
                        handleChangeEmployment={handleChangeEmployment}
                        handleChangeIdentifierDeclaration={handleChangeIdentifierDeclaration}
                        handleChangePractitioner={handleChangePractitioner}
                        organizationId={organizationId}
                        organizationType={organizationType}
                        practitionerRegistrationCodes={pick(practitioner, [
                            'identifierDeclaration',
                            'identifierRegistrationBig',
                            'identifierRegistrationKp',
                        ])}
                        practitionerWillUseCS={!!practitioner.emailAddress}
                        profilePractitionerRegistrationCodes={profilePractitionerRegistrationCodes}
                    />
                </>

                <ConfirmationModal
                    handleNo={() => blocker.reset?.()}
                    handleYes={() => {
                        blocker.proceed?.();
                    }}
                    isOpen={blocker.state === 'blocked'}
                    question={translate.practitioner.confirmCancelMessage}
                    title={translate.practitioner.confirmCancelAddKsyosEmployeeTitle}
                />
            </OverlayTemplate>
        );
    }

    function handleChangeEmployment(update: Partial<EmploymentAdd>) {
        const employmentUpdate = { ...employment, ...update };
        setEmployment(employmentUpdate);
    }

    function handleChangeIdentifierDeclaration<K extends keyof IIdentifierDeclaration>(
        name: K,
        value: IIdentifierDeclaration[K]
    ) {
        setPractitioner({ ...practitioner, [name]: value });
    }

    function handleChangePractitioner(update: Partial<IRegistrationCodesAdd>) {
        setPractitioner((current) => ({ ...current, ...update }));
    }

    async function validateOnChange(
        identifierRegistrationCodesToValidate?: (keyof PractitionerRegistrationCodesValidated)[]
    ) {
        const validationResult = validate(practitionerEmploymentFrontendSchema, {
            employment,
            organizationId,
            organizationType,
            practitionerRegistrationCodes: pick(practitioner, [
                'identifierDeclaration',
                'identifierRegistrationBig',
                'identifierRegistrationKp',
            ]),
            practitionerWillUseCS: !!practitioner.emailAddress,
        });

        let duplicatedRegistrationCodeErrors = emptyValidationErrors;
        if (
            identifierRegistrationCodesToValidate &&
            !areThereRegistrationCodeErrors(validationResult.errors, identifierRegistrationCodesToValidate)
        ) {
            duplicatedRegistrationCodeErrors = await checkDuplicatedRegistrationCodes(
                practitioner,
                id,
                identifierRegistrationCodesToValidate
            );
        }
        setErrors(validationResult.errors.merge(duplicatedRegistrationCodeErrors));
    }

    async function checkOverlap(selectedOrganizationId?: string, employmentUpdate?: EmploymentAdd) {
        const currentEmployment = employmentUpdate ?? employment;
        if (selectedOrganizationId && employment.startDate) {
            const currentOverlap = await overlapChecker.check(
                { organizationId: selectedOrganizationId },
                currentEmployment.startDate
            );
            setOverlap(currentOverlap);
        } else {
            setOverlap(null);
        }
    }

    async function validateAndSaveEmployment() {
        await checkOverlap(organizationId);
        setIsValidatingAndWantsToSave(true);
    }

    async function save() {
        setAreValidationsActivated(true);
        setIsSaving(true);
        const validationResult = validate(practitionerEmploymentFrontendSchema, {
            employment: {
                ...employment,
                specialismCode: getSpecialismCodeByProfession(employment.profession, employment.specialismCode),
            },
            organizationId,
            organizationType,
            practitionerRegistrationCodes: pick(practitioner, [
                'identifierDeclaration',
                'identifierRegistrationBig',
                'identifierRegistrationKp',
            ]),
            practitionerWillUseCS: !!practitioner.emailAddress,
        });

        if (!validationResult.hasErrors) {
            const paddedIdentifierRegistrationBig = padStartBigCode(
                validationResult.item.practitionerRegistrationCodes.identifierRegistrationBig
            );

            const result = await abortableFetch.fetchJson(
                `/api/practitioners/${id}/employments?isExistingPractitioner=true`,
                'POST',
                {
                    ...validationResult.item,
                    practitionerRegistrationCodes: {
                        ...validationResult.item.practitionerRegistrationCodes,
                        identifierRegistrationBig: paddedIdentifierRegistrationBig,
                    },
                },
                [
                    ValidationErrorCode.IDENTIFIER_REGISTRATION_BIG_EXISTS,
                    ValidationErrorCode.IDENTIFIER_REGISTRATION_KP_EXISTS,
                    ValidationErrorCode.IDENTIFIER_DECLARATION_EXISTS,
                ]
            );

            if (result.errorCode === ValidationErrorCode.IDENTIFIER_REGISTRATION_BIG_EXISTS) {
                setErrors(errors.set('practitioner.identifierRegistrationBig', result.errorCode));
            } else if (result.errorCode === ValidationErrorCode.IDENTIFIER_REGISTRATION_KP_EXISTS) {
                setErrors(errors.set('practitioner.identifierRegistrationKp', result.errorCode));
            } else if (result.errorCode === ValidationErrorCode.IDENTIFIER_DECLARATION_EXISTS) {
                setErrors(errors.set('practitioner.identifierDeclaration', result.errorCode));
            } else {
                setIsBlocked(false);
                setShowSuccessPage(true);
                setPractitioner({
                    ...practitioner,
                    identifierRegistrationBig: paddedIdentifierRegistrationBig,
                });
            }
        } else {
            setErrors(validationResult.errors);
        }

        setIsSaving(false);
        setIsValidatingAndWantsToSave(false);
    }

    async function loadPractitioner() {
        const url = `/api/practitioners/${id}?includeProfileThumbnail=true`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setPractitioner(result.json);
        setProfilePractitionerRegistrationCodes({
            identifierDeclaration: result.json.identifierDeclaration,
            identifierRegistrationBig: result.json.identifierRegistrationBig,
            identifierRegistrationKp: result.json.identifierRegistrationKp,
        });
    }

    async function loadOrganizationType() {
        const url = `/api/organizations/${organizationId}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        const organizationTypeWithLinkedProfessions = convertOrganizationTypeToOrganizationTypeWithLinkedProfessions(
            result.json.organizationType
        );
        setOrganizationType(organizationTypeWithLinkedProfessions);
    }
}
